<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card id="report-card">
            <div id="report-body" class="w-100" v-if="result.length > 0">
                <QTable
                    :data="result"
                    :columns="formattedColumns"
                    virtual-scroll
                    :virtual-scroll-item-size="20"
                    :virtual-scroll-sticky-size-start="20"
                    :rows-per-page-options="[0]"
                    row-key="item_id"
                    :hide-bottom="items.length === 0"
                    binary-state-sort
                    :loading="isLoading"
                >
                    <template v-slot:header-cell="{ col, sort }">
                        <th
                            @click="col.sortable ? sort(col) : null"
                            :class="{
                                'text-left': col.align === 'left',
                                'text-center': col.align === 'center',
                                'text-right': col.align === 'right',
                                sortable: col.sortable
                            }"
                        >
                            {{ col.label }}
                            <i
                                v-if="col.__thClass.includes('sort-desc')"
                                class="cus-bullet-arrow-down"
                            />
                            <i
                                v-else-if="col.__thClass.includes('sorted')"
                                class="cus-bullet-arrow-up"
                            />
                        </th>
                    </template>
                    <template v-slot:body-cell-name="{ row }">
                        <td>
                            <b v-if="isSection('name', row.name)">
                                {{ row.name.slice(0, -8) }}
                            </b>
                            <b v-else-if="isTotal('name', row.name)">
                                {{ row.name }}
                            </b>
                            <span v-else>
                                {{ properDescription(row.name) }}
                            </span>
                        </td>
                    </template>
                    <template v-slot:body-cell="{ col, row }">
                        <td>
                            <b v-if="isSection('name', row.name)"> </b>
                            <b
                                v-else-if="
                                    isTotal('name', row.name) ||
                                    col.name === 'Period Total'
                                "
                            >
                                {{ _toCurrency(row[col.name]) }}
                            </b>
                            <span v-else>
                                {{ _toCurrency(row[col.name]) }}
                            </span>
                        </td>
                    </template>
                </QTable>
            </div>
            <div v-else class="w-100 pt-2 pb-2">
                <div class="w-100" v-if="isLoading">
                    <QTable
                        :columns="getColumnsDummyTable()"
                        :loading="true"
                        :data="[]"
                        hide-bottom
                        class="q-table"
                    />
                </div>
                <i v-else-if="message">{{ message }}</i>
                <i v-else>No records available</i>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import "@/css/quasar-table.css";
import { forEach, isEmpty } from "lodash";
export default {
    mixins: [Report],
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: moment(),
                    end: moment()
                };
            }
        }
    },
    data() {
        return {
            result: [],
            tableHeader: [],
            dateRange: "",
            isLoading: false,
            pageSize: 20,
            lastPage: 0,
            pagination: {},
            columns: [],
            items: [],
            refresh: 0,
            title: "End of day Reconciliation",
            message: null
        };
    },
    computed: {
        formattedColumns() {
            let columns = [];

            forEach(this.tableHeader, function (header) {
                if (header === "name") {
                    columns.push({
                        name: "name",
                        label: "",
                        field: "name",
                        align: "left"
                    });
                } else {
                    columns.push({
                        name: header,
                        label: header,
                        field: header,
                        align: "left"
                    });
                }
            });

            return columns;
        }
    },
    mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.filterDateOption = true;
        self.data.isShowProvider = false;
        self.data.isShowDateRange = false;
        self.data.isShowDateTimeRange = true;
        self.data.datePickerOptionDefault = 1;
        self._getSetting().then(async () => {
            await self.initSetDates();
            self.toggleOptions(false);
            self.init();
        });

        self.$root.$on("report-filter-date-range:refresh", self.init);
        self.$root.$on("end-of-day-reconciliation:refresh", function () {
            self.refresh = 1;
            self.init();
        });

        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.datePickerOptionDefault = null;
        this.data.isShowDateTimeRange = false;
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        toggleOptions(value) {
            this.defaultToggleOptions(value);
        },
        async initSetDates() {
            let query = this.$route.query;
            let self = this;

            if (!isEmpty(query.to) && !isEmpty(query.from)) {
                self.data.start = moment.unix(query.from).tz(self._timeZone);
                self.data.end = moment.unix(query.to).tz(self._timeZone);
                self.data.from = query.from;
                self.data.to = query.to;
                self.dateRange = self.getDateRange(query.from, query.to);
            } else {
                self.data.start = self.getStartDate("day");
                self.data.end = self.getEndDate("day");
                self.data.from = self.converToUnix(self.data.start);
                self.data.to = self.converToUnix(self.data.end);
            }
        },
        async init(params) {
            let self = this;
            self.isLoading = true;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
                self.refresh = params.refresh;
            }
            self.dateRange = self.getDateTimeRange(
                self.data.from,
                self.data.to
            );

            await ReportService.getReport(CREPORT.TYPE.EOD_RECONCILIATION, {
                from: self.data.from,
                to: self.data.to,
                refresh: self.refresh
            })
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        const resource = res.resource;
                        self.result = resource.results;
                        self.title = resource.meta.title;
                        self.tableHeader = resource.header;

                        self.dateRange = self.getDateTimeRange(
                            resource.meta.fromDate,
                            resource.meta.toDate
                        );

                        if (resource.meta.fromDate && resource.meta.toDate) {
                            self.data.start = moment.unix(
                                parseFloat(resource.meta.fromDate)
                            );
                            self.data.end = moment.unix(
                                parseFloat(resource.meta.toDate)
                            );

                            self.data.from = parseFloat(resource.meta.fromDate);
                            self.data.to = parseFloat(resource.meta.toDate);
                        }
                        self.message = null;
                    } else if (res && res.queue) {
                        let dateTo = self.data.to;
                        let dateFrom = self.data.from;
                        let query = self.$route.query;

                        self._message(res.message, "warning");
                        self.message = res.message;
                        if (
                            !(
                                query &&
                                query.to == dateTo &&
                                query.from == dateFrom
                            )
                        ) {
                            self.$router.replace({
                                query: { to: dateTo, from: dateFrom }
                            });
                        }
                    } else {
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                        self.message = null;
                    }
                })
                .catch(e => {
                    self.isLoading = false;
                    this._message("Error: Unable to get the report", "danger");
                    console.error(e);
                })
                .finally(() => {
                    self.toggleOptions(true);
                    self.isLoading = false;
                    self.dynamicPageTitle(self.title);
                });
        },
        getColumnsDummyTable() {
            let startDate = this._now().toFormat(this._dateFormat);

            return this.getDummyColumns([" ", startDate, "Period Total"]);
        },
        getDummyColumns(array = []) {
            let columns = [];

            forEach(array, function (header, key) {
                columns.push({
                    name: isEmpty(header) ? "something" + key : header,
                    label: header,
                    field: "something",
                    align: "left"
                });
            });

            return columns;
        },
        isSection(key, value) {
            if (key === "name" && value.substr(-8) === "_section") {
                return true;
            }

            return false;
        },
        isSectionValue(item) {
            let description = item["name"];
            if (description.substr(-8) === "_section") {
                return true;
            }
            return false;
        },
        isTotalValue(item) {
            let description = item["name"];
            if (description.substr(0, 6) === "Total ") {
                return true;
            }
            return false;
        },
        isTotal(key, value) {
            if (key === "name" && value.substr(0, 6) === "Total ") {
                return true;
            }
            return false;
        },
        properDescription(value = "") {
            let str = value.trim();

            if (str.substr(-7) === "_refund") {
                return str.slice(0, -7);
            } else {
                return value;
            }
        }
    }
};
</script>
<style>
.total tr:last-child {
    font-weight: bold;
}
</style>
